import { useState, useRef, useCallback } from 'react';

export interface UploadedFile {
  id: string;
  file: File;
  preview: string;
}

export interface UseFileUploadOptions {
  accept?: string;
  maxSize?: number;
  multiple?: boolean;
}

export interface FileUploadState {
  files: UploadedFile[];
}

export interface FileUploadActions {
  removeFile: (id: string) => void;
  openFileDialog: () => void;
  getInputProps: () => {
    type: string;
    accept?: string;
    multiple?: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
}

export function useFileUpload(
  options: UseFileUploadOptions = {}
): [FileUploadState, FileUploadActions] {
  const { accept = 'image/*', maxSize, multiple = false } = options;
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);

      if (selectedFiles.length === 0) return;

      const newFiles: UploadedFile[] = selectedFiles.map((file) => {
        // Check file size if maxSize is specified
        if (maxSize && file.size > maxSize) {
          console.warn(`File ${file.name} exceeds maximum size of ${maxSize} bytes`);
          return null;
        }

        // Create preview URL for images
        const preview = URL.createObjectURL(file);

        return {
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          file,
          preview,
        };
      }).filter(Boolean) as UploadedFile[];

      setFiles((prevFiles) => {
        // Clean up old preview URLs
        prevFiles.forEach((f) => URL.revokeObjectURL(f.preview));

        return multiple ? [...prevFiles, ...newFiles] : newFiles;
      });

      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [maxSize, multiple]
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prevFiles) => {
      const fileToRemove = prevFiles.find((f) => f.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prevFiles.filter((f) => f.id !== id);
    });
  }, []);

  const openFileDialog = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const getInputProps = useCallback(() => {
    return {
      type: 'file' as const,
      accept,
      multiple,
      onChange: handleFileChange,
      ref: (ref: HTMLInputElement | null) => {
        inputRef.current = ref;
      },
    };
  }, [accept, multiple, handleFileChange]);

  return [
    { files },
    { removeFile, openFileDialog, getInputProps },
  ];
}
